import { syncUserWithBackend, isBannedUserError } from '@/api/auth';
import { clearAuthSession, isAccountLinkRequiredError, isEmailUnverifiedError } from '@/lib/accountLinking';
import { deregisterApnsToken } from '@/api/pushNotifications';
import User from '@/models/user';
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { isDefinitiveAuthFailure } from '@/lib/authFailure';
import { refreshAccessToken } from '@/lib/refreshAccessToken';
import { runWithProfileSyncRetry } from '@/lib/syncUserProfileWithRetry';
import { decodeIdToken, isTokenExpired } from '@/utils/tokenUtils';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_ONBOARDING_COMPLETED } from '@/lib/analyticsEvents';
import { debug, log, warn, error } from '@/utils/logger';
import axios from 'axios';
import { getPlatform } from '@/lib/platform';
import { clearOnboardingDeferral } from '@/lib/onboardingDeferral';
import BannedUserDialog from '@/components/BannedUserDialog/BannedUserDialog';
import {
    USER_COUNTRIES_COUNT_UPDATED_EVENT,
    userWithCountryCount,
    type UserCountriesCountUpdatedDetail,
} from '@/lib/userCountriesCountEvents';

/** Minimal shape returned from the local-only register() helper (not backend-synced). */
interface RegisterStubUser {
    id: string;
    name: string;
    email?: string;
}

interface BackendUser {
    _id: string;
    email: string;
    cognito_username: string;
    name?: string;
    /**
     * Public profile handle. Optional; users can set it at signup or from the
     * profile editor. When present, `buildProfileHref(user)` prefers this over
     * the Mongo `_id` when constructing `/profile/...` URLs.
     */
    username?: string;
    profile_type?: string;
    did_complete_onboarding?: boolean;
    is_internal?: boolean;
    [key: string]: unknown; // Allow other fields from backend
}

function normalizeLoginPayload(userData: User | BackendUser): BackendUser {
    if (userData instanceof User) {
        // `syncUserWithBackend` returns a User class instance. Serialize to a plain object so
        // avatar URLs and the rest of the API payload survive `setUser` / localStorage, matching
        // pre–normalizeLoginPayload behavior (the previous pick list dropped image fields).
        return JSON.parse(JSON.stringify(userData)) as BackendUser;
    }
    return userData;
}

interface AuthContextValue {
    user: BackendUser | null;
    isLoading: boolean;
    isInitialized: boolean;
    isAuthenticated: boolean;
    login: (userData?: BackendUser | User) => void;
    register: (data: { username: string; email?: string }) => Promise<RegisterStubUser>;
    logout: () => Promise<void>;
    updateUser: (userData: Partial<BackendUser>) => void;
    hasOnboarded: boolean;
    completeOnboarding: () => void;
    isLoggedIn: boolean;
}

// Keep the same context object across Vite hot reloads. A new createContext()
// on each reload disconnects useAuth from the mounted provider and the app
// looks signed out until a full refresh.
const hotData = import.meta.hot?.data;
const hotAuthContext = hotData?.authContext as
    | React.Context<AuthContextValue | undefined>
    | undefined;
const AuthContext = hotAuthContext ?? createContext<AuthContextValue | undefined>(undefined);
if (hotData) {
    hotData.authContext = AuthContext;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<BackendUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasOnboarded, setHasOnboarded] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showBannedDialog, setShowBannedDialog] = useState(false);
    const [bannedMessage, setBannedMessage] = useState('');
    const [isDeletedAccount, setIsDeletedAccount] = useState(false);

    // Helper function to attempt token refresh.
    // First tries Amplify (works for email/password users where amazon-cognito-identity-js
    // wrote tokens in Cognito-compatible format). Falls back to a direct Cognito token
    // endpoint call using the raw refresh token stored in localStorage, which works for
    // all login flows including OAuth (Google/Apple) users.
    const tryRefreshTokens = useCallback(async (): Promise<
        | { ok: true; accessToken: string; idToken: string }
        | { ok: false; shouldLogout: boolean }
    > => {
        const result = await refreshAccessToken('AuthContext');
        if (result.ok) {
            const idToken = localStorage.getItem('idToken') ?? '';
            return { ok: true, accessToken: result.accessToken, idToken };
        }
        return { ok: false, shouldLogout: result.shouldLogout };
    }, []);

    // Helper function to clear auth state when tokens expire
    const clearAuthStateOnTokenExpiry = useCallback(async (logoutFn: () => Promise<void>) => {
        log('🔐 [AuthContext] Token expired - clearing auth state');
        // Clear user state that was set from localStorage
        setUser(null);
        setIsLoggedIn(false);
        setHasOnboarded(false);
        // Clear all auth data
        await logoutFn();
    }, []);

    // Helper function to handle auth errors (401/403 from /user/authenticated)
    const handleAuthError = useCallback(async (
        err: unknown,
        logoutFn: () => Promise<void>
    ): Promise<boolean> => {
        // Type guard for axios errors
        const isAxiosError = axios.isAxiosError(err);
        const responseStatus = isAxiosError ? err.response?.status : undefined;
        const requestUrl = isAxiosError ? err.config?.url : undefined;
        
        // If syncUserWithBackend fails with 401/403, session is invalid
        // Force logout and redirect to logged-out view
        const isAuthError = responseStatus === 401 || responseStatus === 403;
        const isAuthEndpoint = requestUrl?.includes('/user/authenticated');
        
        if (isAuthError && isAuthEndpoint) {
            const isDefinitiveAuthError = isDefinitiveAuthFailure(responseStatus, isAxiosError ? err.response?.data : undefined);
            if (!isDefinitiveAuthError) {
                debug('🔐 [AuthContext] Ambiguous auth endpoint failure - preserving session', {
                    status: responseStatus,
                    endpoint: requestUrl,
                });
                return false;
            }
            debug('🔐 [AuthContext] Session expired - forcing logout and redirect', {
                status: responseStatus,
                endpoint: requestUrl
            });
            // Clear user state first (was set from localStorage before sync)
            setUser(null);
            setIsLoggedIn(false);
            setHasOnboarded(false);
            // Clear all auth state and redirect
            await logoutFn();
            // Redirect to logged-out view
            if (typeof window !== 'undefined') {
                window.location.href = '/';
            }
            return true; // Indicates error was handled
        }
        
        return false; // Error was not an auth error
    }, []);

    // Define login before useEffect so it can be used in the dependency array
    const login = useCallback(async (userData?: BackendUser | User) => {
        if (userData) {
            const payload = normalizeLoginPayload(userData);
            try {
                localStorage.setItem('authUser', JSON.stringify(payload));
                setUser(payload);

                // Identify user in Mixpanel
                if (payload._id) {
                    WebAnalytics.identify(payload._id);
                    WebAnalytics.setUserProfile({
                        id: payload._id,
                        email: payload.email,
                        name: payload.name ?? '',
                        profile_type: payload.profile_type || 'regular',
                        is_internal: payload.is_internal === true,
                    });
                }
            } catch (e) {
                warn('Failed to save user to localStorage:', e);
            }
        }
        setIsLoggedIn(true);
        try {
            localStorage.setItem('isLoggedIn', 'true');
        } catch (e) {
            warn('Failed to save isLoggedIn to localStorage:', e);
        }
    }, []);

    const logout = useCallback(async () => {
        if (getPlatform() === 'ios') {
            try {
                await deregisterApnsToken();
            } catch (err) {
                warn('🔐 [AuthContext] Failed to deregister APNS token on logout', err);
            }
        }

        // Clear only auth-related keys to preserve other application state
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('hasOnboarded');
        clearOnboardingDeferral();
        setIsLoggedIn(false);
        setUser(null);
        setHasOnboarded(false);
        WebAnalytics.reset();
    }, []);

    useEffect(() => {
        const onCountriesCountUpdated = (event: Event) => {
            const detail = (event as CustomEvent<UserCountriesCountUpdatedDetail>).detail;
            if (!detail?.userId) return;
            setUser((prev) => {
                const next = userWithCountryCount(prev, detail);
                if (!next || next === prev) return prev;
                try {
                    localStorage.setItem('authUser', JSON.stringify(next));
                } catch (e) {
                    warn('Failed to persist countries_count on authUser:', e);
                }
                return next;
            });
        };
        window.addEventListener(USER_COUNTRIES_COUNT_UPDATED_EVENT, onCountriesCountUpdated);
        return () => {
            window.removeEventListener(USER_COUNTRIES_COUNT_UPDATED_EVENT, onCountriesCountUpdated);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const initializeAuth = async () => {
            try {
            const token = localStorage.getItem('accessToken');
            const idToken = localStorage.getItem('idToken');
            const storedUser = localStorage.getItem('authUser');
            const onboarded = localStorage.getItem('hasOnboarded') === 'true';
            setHasOnboarded(onboarded);
            setIsLoggedIn(localStorage.getItem('isLoggedIn') === 'true');

            if (storedUser) {
                try {
                    setUser(JSON.parse(storedUser));
                } catch (e) {
                    error('Error parsing stored user:', e);
                }
            }

            if (token && idToken) {
                let currentIdToken = idToken;

                // Check token expiration before making API calls
                if (isTokenExpired(token) || isTokenExpired(idToken)) {
                    log('🔐 [AuthContext] Token expired on initialization - attempting refresh', {
                        accessTokenExpired: isTokenExpired(token),
                        idTokenExpired: isTokenExpired(idToken)
                    });

                    // Try to refresh tokens before giving up
                    const refreshedTokens = await tryRefreshTokens();
                    if (cancelled) return;

                    if (refreshedTokens.ok) {
                        currentIdToken = refreshedTokens.idToken;
                        log('🔐 [AuthContext] Using refreshed tokens for initialization');
                    } else if (refreshedTokens.shouldLogout) {
                        log('🔐 [AuthContext] Token refresh rejected - clearing auth state');
                        await clearAuthStateOnTokenExpiry(logout);
                        return;
                    } else {
                        warn('🔐 [AuthContext] Token refresh failed transiently - keeping session');
                    }
                }

                try {
                    // Decode ID token using utility function (use currentIdToken which may be refreshed)
                    const { email, cognitoUsername } = decodeIdToken(currentIdToken);

                    if (email && cognitoUsername) {
                        // Use the authenticated endpoint (same as mobile app)
                        const backendUser = await runWithProfileSyncRetry(
                            () => syncUserWithBackend(email, cognitoUsername),
                            'AuthContext.init',
                        );
                        
                        if (cancelled) return;

                        // Validate user object before using it
                        if (backendUser && typeof backendUser === 'object' && Object.keys(backendUser).length > 0) {
                            // Set onboarding state from backend response (authoritative source)
                            debug('🔐 [AuthContext] Setting onboarding state', {
                                didCompleteOnboarding: backendUser.did_complete_onboarding,
                                userId: backendUser._id,
                                email: backendUser.email
                            });
                            
                            if (backendUser.did_complete_onboarding) {
                                debug('🔐 [AuthContext] User has completed onboarding - setting hasOnboarded to true');
                                setHasOnboarded(true);
                                try {
                                    localStorage.setItem('hasOnboarded', 'true');
                                } catch (e) {
                                    warn('Failed to save hasOnboarded to localStorage:', e);
                                }
                            } else {
                                debug('🔐 [AuthContext] User has NOT completed onboarding - setting hasOnboarded to false');
                                setHasOnboarded(false);
                                try {
                                    localStorage.removeItem('hasOnboarded');
                                } catch (e) {
                                    warn('Failed to remove hasOnboarded from localStorage:', e);
                                }
                            }
                            
                            login(backendUser);
                        } else {
                            error('Invalid user data received from backend');
                            try {
                                localStorage.removeItem('accessToken');
                                localStorage.removeItem('idToken');
                            } catch (e) {
                                warn('Failed to clear tokens from localStorage:', e);
                            }
                        }
                    } else {
                        // Cannot extract email/username from token - clear tokens and require re-authentication
                        error('Cannot extract email/username from ID token. Clearing tokens.');
                        try {
                            localStorage.removeItem('accessToken');
                            localStorage.removeItem('idToken');
                        } catch (e) {
                            warn('Failed to clear tokens from localStorage:', e);
                        }
                    }
                } catch (err: unknown) {
                    if (cancelled) return;
                    if (isAccountLinkRequiredError(err) || isEmailUnverifiedError(err)) {
                        clearAuthSession();
                        setUser(null);
                        setIsLoggedIn(false);
                        return;
                    }
                    error('Error syncing user with backend:', err);
                    
                    // Check for banned or deleted account errors first
                    const isAxiosBanned = axios.isAxiosError(err) && err.response?.status === 403 && err.response?.data?.error === 'Account banned';
                    const isAxiosDeleted = axios.isAxiosError(err) && err.response?.status === 403 && err.response?.data?.error === 'Account deleted';
                    const errAny = err as Record<string, unknown>;
                    
                    if (isBannedUserError(err) || errAny?.isBanned || isAxiosBanned) {
                        const message = (errAny?.bannedMessage as string | undefined) || (axios.isAxiosError(err) ? err.response?.data?.message : null) || 'Your account has been suspended. Please contact support for assistance.';
                        // Clear any partial auth state
                        setUser(null);
                        setIsLoggedIn(false);
                        setHasOnboarded(false);
                        await logout();
                        setBannedMessage(message);
                        setIsDeletedAccount(false);
                        setShowBannedDialog(true);
                        return;
                    } else if (errAny?.isDeleted || isAxiosDeleted) {
                        const message = (errAny?.deletedMessage as string | undefined) || (axios.isAxiosError(err) ? err.response?.data?.message : null) || 'This account has been deleted. Please contact support for assistance.';
                        // Clear any partial auth state
                        setUser(null);
                        setIsLoggedIn(false);
                        setHasOnboarded(false);
                        await logout();
                        setBannedMessage(message);
                        setIsDeletedAccount(true);
                        setShowBannedDialog(true);
                        return;
                    }
                    
                    // Definitive auth failures log out inside handleAuthError.
                    // Anything else (network blip, abort, ambiguous 401/403) keeps
                    // the session already restored from localStorage.
                    const wasAuthError = await handleAuthError(err, logout);
                    if (cancelled || wasAuthError) {
                        return;
                    }
                    warn('🔐 [AuthContext] User sync failed without a definitive auth error - keeping session');
                }
            } else if (token) {
                // Check if access token is expired before attempting to fetch session
                if (isTokenExpired(token)) {
                    log('🔐 [AuthContext] Access token expired on initialization - attempting refresh');

                    // Try to refresh tokens before giving up
                    const refreshedTokens = await tryRefreshTokens();
                    if (cancelled) return;

                    if (!refreshedTokens.ok) {
                        if (refreshedTokens.shouldLogout) {
                            log('🔐 [AuthContext] Token refresh rejected - clearing auth state');
                            await clearAuthStateOnTokenExpiry(logout);
                            return;
                        }
                        warn('🔐 [AuthContext] Token refresh failed transiently - keeping session');
                    }
                }

                // If we have accessToken but no idToken, try to get session
                try {
                    const session = await fetchAuthSession();
                    const sessionIdToken = session?.tokens?.idToken?.toString();
                    
                    if (cancelled) return;
                    
                    // Check if the fetched ID token is expired
                    if (sessionIdToken && isTokenExpired(sessionIdToken)) {
                        debug('🔐 [AuthContext] Fetched ID token expired - clearing auth state');
                        await clearAuthStateOnTokenExpiry(logout);
                        return;
                    }

                    if (sessionIdToken) {
                        try {
                            localStorage.setItem('idToken', sessionIdToken);
                        } catch (e) {
                            warn('Failed to save idToken to localStorage:', e);
                        }
                        
                        // Decode using utility function
                        const { email, cognitoUsername } = decodeIdToken(sessionIdToken);

                        if (email && cognitoUsername) {
                            const backendUser = await runWithProfileSyncRetry(
                                () => syncUserWithBackend(email, cognitoUsername),
                                'AuthContext.init',
                            );
                            
                            if (cancelled) return;

                            // Validate user object
                            if (backendUser && typeof backendUser === 'object' && Object.keys(backendUser).length > 0) {
                                if (backendUser.did_complete_onboarding) {
                                    setHasOnboarded(true);
                                    try {
                                        localStorage.setItem('hasOnboarded', 'true');
                                    } catch (e) {
                                        warn('Failed to save hasOnboarded to localStorage:', e);
                                    }
                                }
                                login(backendUser);
                            } else {
                                error('Invalid user data received from backend');
                                try {
                                    localStorage.removeItem('accessToken');
                                    localStorage.removeItem('idToken');
                                } catch (e) {
                                    warn('Failed to clear tokens from localStorage:', e);
                                }
                            }
                        } else {
                            // Cannot extract email/username from token - clear tokens
                            error('Cannot extract email/username from ID token. Clearing tokens.');
                            try {
                                localStorage.removeItem('accessToken');
                                localStorage.removeItem('idToken');
                            } catch (e) {
                                warn('Failed to clear tokens from localStorage:', e);
                            }
                        }
                    } else {
                        // No ID token in session - clear access token
                        error('No ID token in session. Clearing tokens.');
                        try {
                            localStorage.removeItem('accessToken');
                        } catch (e) {
                            warn('Failed to clear accessToken from localStorage:', e);
                        }
                    }
                } catch (err: unknown) {
                    if (cancelled) return;
                    if (isAccountLinkRequiredError(err) || isEmailUnverifiedError(err)) {
                        clearAuthSession();
                        setUser(null);
                        setIsLoggedIn(false);
                        return;
                    }
                    error('Error fetching auth session or syncing user:', err);
                    
                    // Same rule as the token + id token path: only a definitive
                    // auth failure clears the session.
                    const wasAuthError = await handleAuthError(err, logout);
                    if (cancelled || wasAuthError) {
                        return;
                    }
                    warn('🔐 [AuthContext] Session restore failed without a definitive auth error - keeping session');
                }
            } else if (storedUser || localStorage.getItem('isLoggedIn') === 'true') {
                // No usable token, but a persisted session. Trusting `authUser`
                // here boots the app as "signed in" with nothing to authenticate
                // with: the first authenticated request goes out without an
                // Authorization header, 401s, and the axios interceptor's refresh
                // fails (there is no refresh token either) and ends in
                // clearAuthAndRedirect() -> window.location.href = '/'. That is the
                // hard reload users hit on /my-travel/lists right after the
                // skeletons paint. Drop the stale session instead.
                error('Persisted user without tokens on initialization - clearing stale session');
                setUser(null);
                setIsLoggedIn(false);
                setHasOnboarded(false);
                await logout();
            }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsInitialized(true);
                }
            }
        };

        initializeAuth();

        return () => {
            cancelled = true;
        };
        // Once per mount. These callbacks are stable, but listing them restarts
        // this effect on hot reload, aborts the in-flight sync, and can clear
        // a valid session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const register = useCallback(async (userData: { username: string; email?: string }) => {
        setIsLoading(true);
        try {
            if (!userData || !userData.username) {
                throw new Error('Missing registration data');
            }

            const fakeToken = `local-${Date.now()}`;
            const newUser: RegisterStubUser = {
                id: userData.username,
                name: userData.username,
                email: userData.email || ''
            };

            try {
                localStorage.setItem('authToken', fakeToken);
                localStorage.setItem('authUser', JSON.stringify(newUser));
            } catch (e) {
                warn('Failed to save to localStorage:', e);
            }
            
            // Note: register creates a local User, not a BackendUser
            // This is only used for local registration flow, not backend sync
            // We don't set user state here as it expects BackendUser type from syncUserWithBackend

            return newUser;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const updateUser = useCallback((userData: Partial<BackendUser>) => {
        setUser((prev) => (prev ? { ...prev, ...userData } : prev));
    }, []);

    const completeOnboarding = useCallback(() => {
        setHasOnboarded(true);
        localStorage.setItem('hasOnboarded', 'true');
        localStorage.setItem('onboarding_completed_at', new Date().toISOString());
        WebAnalytics.track(EVENT_ONBOARDING_COMPLETED, {
            role: user?.profile_type || 'unknown',
        });
    }, [user]);

    const value = useMemo(
        () => ({
            user,
            isLoading,
            isInitialized,
            isAuthenticated: !!user,
            login,
            register,
            logout,
            updateUser,
            hasOnboarded,
            completeOnboarding,
            isLoggedIn
        }),
        [user, isLoading, isInitialized, hasOnboarded, login, register, logout, updateUser, isLoggedIn, completeOnboarding]
    );

    const handleBannedDialogClose = useCallback(async () => {
        setShowBannedDialog(false);
        setBannedMessage('');
        setIsDeletedAccount(false);
        await logout();
        // Use window.location.href for a full page reload to ensure clean state
        if (typeof window !== 'undefined') {
            window.location.href = '/';
        }
    }, [logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
            {showBannedDialog && (
                <BannedUserDialog
                    message={bannedMessage}
                    onClose={handleBannedDialogClose}
                    isDeleted={isDeletedAccount}
                />
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

export default AuthContext;
